import { API_ENDPOINTS } from '@/config/api.config';
import apiClient from './api';

// SCIM Configuration DTOs
export interface ScimConfigurationDto {
  _id?: string;
  id?: string;
  provider_name: string;
  provider_type: string;
  base_url: string;
  token_endpoint?: string;
  wellknown_url?: string;
  client_id: string;
  client_secret: string;
  scopes?: string[];
  timeout_ms?: number;
  retry_attempts?: number;
  is_active: boolean;
  sync_config?: {
    users: boolean;
    groups: boolean;
    roles: boolean;
    batch_size: number;
    conflict_resolution: string;

    // Automatic periodic sync configuration
    auto_sync_enabled?: boolean;
    sync_frequency?: string;
    sync_type?: 'full' | 'groups' | 'users';
    last_scheduled_sync?: string;
    next_scheduled_sync?: string;
  };
  mapping_rules?: {
    custom_transformations: any[];
  };
  last_sync?: string;
  last_sync_status?: string;
  is_connected?: boolean; // Computed by backend - true if valid token exists
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateScimConfigurationDto {
  provider_name: string;
  provider_type: string;
  base_url: string;
  token_endpoint: string;
  client_id: string;
  client_secret: string;
  scopes: string[];
  sync_config: {
    users: boolean;
    groups: boolean;
    roles: boolean;
    batch_size: number;
    conflict_resolution: string;

    // Automatic periodic sync configuration
    auto_sync_enabled?: boolean;
    sync_frequency?: string;
    sync_type?: 'full' | 'groups' | 'users';
  };
  is_active?: boolean;
}

export interface UpdateScimConfigurationDto {
  provider_name?: string;
  provider_type?: string;
  base_url?: string;
  token_endpoint?: string;
  client_id?: string;
  client_secret?: string;
  scopes?: string[];
  sync_config?: {
    users: boolean;
    groups: boolean;
    roles: boolean;
    batch_size: number;
    conflict_resolution: string;

    // Automatic periodic sync configuration
    auto_sync_enabled?: boolean;
    sync_frequency?: string;
    sync_type?: 'full' | 'groups' | 'users';
    last_scheduled_sync?: string;
    next_scheduled_sync?: string;
  };
  is_active?: boolean;
}

// SCIM Sync Request DTO
export interface ScimSyncRequestDto {
  configuration_id: string;
  sync_type: 'full' | 'groups' | 'users';
  batchSize?: number;
  dryRun?: boolean;
}

// Legacy SCIM Direct Sync Request (deprecated)
export interface ScimDirectSyncRequestDto {
  configId?: string;
  baseUrl?: string;
  accessToken?: string;
  batchSize?: number;
  dryRun?: boolean;
  syncGroups?: boolean;
  syncUsers?: boolean;
}

// SCIM Sync Response
export interface ScimSyncResponseDto {
  syncId: string;
  status: 'INITIATED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'PARTIAL';
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
  summary: {
    totalProcessed: number;
    totalCreated: number;
    totalUpdated: number;
    totalSkipped: number;
    totalFailed: number;
  };
  groups?: {
    total: number;
    created: number;
    updated: number;
    skipped: number;
    failed: number;
  };
  roles?: {
    total: number;
    created: number;
    updated: number;
    skipped: number;
    failed: number;
  };
  groupRoleMaps?: {
    total: number;
    created: number;
    updated: number;
    skipped: number;
    failed: number;
  };
  users?: {
    total: number;
    created: number;
    updated: number;
    skipped: number;
    failed: number;
  };
  errors?: Array<{
    resource: string;
    message: string;
    details?: any;
  }>;
}

// SCIM Sync Log
export interface ScimSyncLogDto {
  _id: string;
  id: string;
  provider_id: string;
  provider_name: string;
  sync_type: string;
  sync_status: string;
  sync_id: string;
  is_dry_run: boolean;

  archive_state?: 'live' | 'archived';

  from_archive?: boolean;
  resources_synced: {
    users: {
      deleted: number;
      total: number;
      created: number;
      updated: number;
      skipped: number;
      failed: number;
    };
    groups: {
      deleted: number;
      total: number;
      created: number;
      updated: number;
      skipped: number;
      failed: number;
    };
    roles: {
      deleted: number;
      total: number;
      created: number;
      updated: number;
      skipped: number;
      failed: number;
    };
  };
  performance: {
    total_duration_ms: number;
    fetch_duration_ms: number;
    transform_duration_ms: number;
    persist_duration_ms: number;
    items_per_second: number;
  };
  conflicts: any[];
  sync_errors: Array<{
    _id: string;
    id: string;
  }>;
  started_at: string;
  completed_at?: string;
  triggered_by: string;
  triggered_by_name?: string;
  notes?: string;
  detailed_changes?: {
    created_items?: Array<{
      resource_type: 'user' | 'group' | 'role' | 'group_role_mapping';
      resource_id: string;
      resource_name: string;
      data: any;
    }>;
    updated_items?: Array<{
      resource_type: 'user' | 'group' | 'role' | 'group_role_mapping';
      resource_id: string;
      resource_name: string;
      old_data: any;
      new_data: any;
      changed_fields: string[];
    }>;
    merged_items?: Array<{
      resource_type: 'user' | 'group' | 'role' | 'group_role_mapping';
      resource_id: string;
      resource_name: string;
      merged_fields: string[];
      data: any;
    }>;
    skipped_items?: Array<{
      resource_type: 'user' | 'group' | 'role' | 'group_role_mapping';
      resource_id: string;
      resource_name: string;
      reason: string;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  code: number;
  description?: string;
  data?: T;
  error?: {
    error_code: string;
    error_description: string;
    meta_data?: any;
  };
}

// SCIM Statistics DTO
export interface ScimStatisticsDto {
  configurations: {
    total: number;
    active: number;
    connected: number;
    disconnected: number;
  };
  syncs: {
    total: number;
    last24Hours: number;
    successful: number;
    failed: number;
    successRate: number;
  };
  performance: {
    avgDuration: number;
    avgItemsPerSecond: number;
    lastSyncTime: string | null;
  };
  topErrors: Array<{
    errorCode: string;
    count: number;
  }>;
}

// List response for configurations
export interface ScimConfigurationListResponse {
  success: boolean;
  code: number;
  description?: string;
  data: ScimConfigurationDto[];
  meta_data: {
    total_records: number;
    current_page: number;
    records_per_page: number;
  };
}

// List response for sync logs
export interface ScimSyncLogListResponse {
  success: boolean;
  code: number;
  description: string;
  data: ScimSyncLogDto[];
  meta_data: {
    total_records: number;
    current_page: number;
    records_per_page: number;
  };
}

/**
 * SCIM Service
 * Handles SCIM configuration and synchronization operations
 */
class ScimService {
  // Using centralized API endpoints from config

  // ============= Configuration Management =============

  /**
   * List all SCIM configurations with filters
   */
  async getConfigurations(filters?: {
    page?: number;
    limit?: number;
    provider_type?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<ScimConfigurationListResponse> {
    try {
      const response = await apiClient.post<ScimConfigurationListResponse>(
        API_ENDPOINTS.SCIM.LIST,
        filters || { page: 1, limit: 10 }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching configurations:', error);
      throw new Error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to fetch configurations'
      );
    }
  }

  /**
   * Get a specific SCIM configuration by ID
   */
  async getConfiguration(id: string): Promise<ApiResponse<ScimConfigurationDto>> {
    try {
      const response = await apiClient.get<ApiResponse<ScimConfigurationDto>>(
        `${API_ENDPOINTS.SCIM.CONFIG}/${id}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching configuration:', error);
      throw new Error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to fetch configuration'
      );
    }
  }

  /**
   * Create a new SCIM configuration
   */
  async createConfiguration(config: CreateScimConfigurationDto): Promise<ScimConfigurationDto> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        code: number;
        description?: string;
        data: ScimConfigurationDto;
      }>(
        API_ENDPOINTS.SCIM.CONFIG,
        config
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to create configuration');
    } catch (error: any) {
      console.error('Error creating configuration:', error);
      throw new Error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to create configuration'
      );
    }
  }

  /**
   * Update an existing SCIM configuration
   */
  async updateConfiguration(id: string, config: UpdateScimConfigurationDto): Promise<ScimConfigurationDto> {
    try {
      const response = await apiClient.put<{
        success: boolean;
        code: number;
        description?: string;
        data: ScimConfigurationDto;
      }>(
        `${API_ENDPOINTS.SCIM.CONFIG}/${id}`,
        config
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to update configuration');
    } catch (error: any) {
      console.error('Error updating configuration:', error);
      throw new Error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to update configuration'
      );
    }
  }

  /**
   * Delete a SCIM configuration
   */
  async deleteConfiguration(id: string): Promise<void> {
    try {
      const response = await apiClient.delete<{
        success: boolean;
        code: number;
        description?: string;
      }>(
        `${API_ENDPOINTS.SCIM.CONFIG}/${id}`
      );
      if (!response.data.success && response.status !== 204) {
        throw new Error(response.data.description || 'Failed to delete configuration');
      }
    } catch (error: any) {
      console.error('Error deleting configuration:', error);
      throw new Error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to delete configuration'
      );
    }
  }

  // ============= Sync Operations =============

  /**
   * Trigger SCIM sync with specified type
   */
  async triggerSync(request: ScimSyncRequestDto): Promise<ApiResponse<ScimSyncResponseDto>> {
    try {
      const response = await apiClient.post<ApiResponse<ScimSyncResponseDto>>(
        API_ENDPOINTS.SCIM.FULL_SYNC,
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Error triggering sync:', error);
      throw new Error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to trigger sync'
      );
    }
  }

  /**
   * Trigger full SCIM sync (Groups, Roles, GroupRoleMaps, and Users)
   * @deprecated Use triggerSync with sync_type: 'full'
   */
  async triggerFullSync(request: ScimDirectSyncRequestDto): Promise<ApiResponse<ScimSyncResponseDto>> {
    return this.triggerSync({
      configuration_id: request.configId || '',
      sync_type: 'full',
      batchSize: request.batchSize,
      dryRun: request.dryRun
    });
  }

  /**
   * Sync only Groups, Roles, and GroupRoleMaps  
   * @deprecated Use triggerSync with sync_type: 'groups'
   */
  async syncGroupsAndRoles(request: ScimDirectSyncRequestDto): Promise<ApiResponse<ScimSyncResponseDto>> {
    return this.triggerSync({
      configuration_id: request.configId || '',
      sync_type: 'groups',
      batchSize: request.batchSize,
      dryRun: request.dryRun
    });
  }

  /**
   * Sync only Users
   * @deprecated Use triggerSync with sync_type: 'users'
   */
  async syncUsersOnly(request: ScimDirectSyncRequestDto): Promise<ApiResponse<ScimSyncResponseDto>> {
    return this.triggerSync({
      configuration_id: request.configId || '',
      sync_type: 'users',
      batchSize: request.batchSize,
      dryRun: request.dryRun
    });
  }

  /**
   * Test SCIM connection / Connect to SCIM provider
   */
  async testConnection(request: { configuration_id: string }): Promise<ApiResponse<{
    success: boolean;
    message: string;
    token: string;
    expires_in: number;
  }>> {
    try {
      const response = await apiClient.post<ApiResponse<{
        success: boolean;
        message: string;
        token: string;
        expires_in: number;
      }>>(
        API_ENDPOINTS.SCIM.TEST_CONNECTION,
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Error testing connection:', error);
      // Return error response instead of throwing
      return {
        success: false,
        code: error?.response?.status || 500,
        error: {
          error_code: 'CONNECTION_TEST_FAILED',
          error_description: error?.response?.data?.error?.error_description ||
                           error?.response?.data?.description ||
                           error?.message ||
                           'Failed to test connection'
        }
      };
    }
  }

  // ============= Sync Logs =============

  /**
   * Get sync history
   */
  async getSyncLogs(params?: {
    page?: number;
    limit?: number;
    provider_id?: string;
    sync_type?: string;
    sync_status?: string;
    is_dry_run?: boolean;
    include_archived?: boolean;
  }): Promise<ScimSyncLogListResponse> {
    try {
      const response = await apiClient.post<ScimSyncLogListResponse>(
        API_ENDPOINTS.SCIM.LIST_SYNC_LOGS,
        params || { page: 1, limit: 20 }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching sync logs:', error);
      throw new Error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to fetch sync logs'
      );
    }
  }

  /**
   * Get a specific sync log by ID
   */
  async getSyncLog(id: string): Promise<ApiResponse<ScimSyncLogDto>> {
    try {
      const response = await apiClient.get<ApiResponse<ScimSyncLogDto>>(
        API_ENDPOINTS.SCIM.GET_SYNC_LOG(id)
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching sync log:', error);
      throw new Error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to fetch sync log'
      );
    }
  }

  /**
   * Cleanup old sync logs
   */
  async cleanupSyncLogs(retentionDays?: number): Promise<ApiResponse<{ deletedCount: number }>> {
    try {
      const response = await apiClient.delete<ApiResponse<{ deletedCount: number }>>(
        API_ENDPOINTS.SCIM.CLEANUP_SYNC_LOGS,
        { params: { retentionDays } }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error cleaning up sync logs:', error);
      throw new Error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to cleanup sync logs'
      );
    }
  }

  // ============= Statistics =============

  /**
   * Get dashboard statistics
   * Returns aggregated metrics for configurations and sync operations
   */
  async getStatistics(): Promise<ApiResponse<ScimStatisticsDto>> {
    try {
      const response = await apiClient.get<ApiResponse<ScimStatisticsDto>>(
        API_ENDPOINTS.SCIM.STATISTICS
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching statistics:', error);
      throw new Error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to fetch statistics'
      );
    }
  }

  // ============= Helper Methods =============

  /**
   * Validate configuration
   */
  validateConfig(config: Partial<ScimDirectSyncRequestDto>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.configId && !config.baseUrl) {
      errors.push('Either Configuration ID or Base URL is required');
    }

    if (config.baseUrl && !config.baseUrl.startsWith('https://')) {
      errors.push('Base URL must use HTTPS');
    }

    if (!config.configId && !config.accessToken) {
      errors.push('Access Token is required when not using a saved configuration');
    }

    if (config.batchSize && (config.batchSize < 1 || config.batchSize > 1000)) {
      errors.push('Batch size must be between 1 and 1000');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Format duration for display
   */
  formatDuration(durationMs?: number): string {
    if (!durationMs) return '-';
    
    if (durationMs < 1000) {
      return `${durationMs}ms`;
    } else if (durationMs < 60000) {
      return `${(durationMs / 1000).toFixed(1)}s`;
    } else {
      const minutes = Math.floor(durationMs / 60000);
      const seconds = Math.floor((durationMs % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
  }

  /**
   * Get sync status color
   */
  getSyncStatusColor(status: string): 'green' | 'red' | 'yellow' | 'blue' {
    switch (status) {
      case 'COMPLETED':
        return 'green';
      case 'FAILED':
        return 'red';
      case 'PARTIAL':
        return 'yellow';
      case 'IN_PROGRESS':
      case 'INITIATED':
        return 'blue';
      default:
        return 'blue';
    }
  }

  /**
   * Get sync summary text
   */
  getSyncSummary(sync: ScimSyncResponseDto | ScimSyncLogDto): string {
    const summary = 'summary' in sync ? sync.summary : {
      totalProcessed: 0,
      totalCreated: 0,
      totalUpdated: 0,
      totalSkipped: 0,
      totalFailed: 0
    };

    const parts: string[] = [];
    
    if (summary.totalProcessed > 0) {
      parts.push(`${summary.totalProcessed} processed`);
    }
    if (summary.totalCreated > 0) {
      parts.push(`${summary.totalCreated} created`);
    }
    if (summary.totalUpdated > 0) {
      parts.push(`${summary.totalUpdated} updated`);
    }
    if (summary.totalFailed > 0) {
      parts.push(`${summary.totalFailed} failed`);
    }

    return parts.join(', ') || 'No changes';
  }
}

// Export singleton instance
export const scimService = new ScimService();
export default scimService;